import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Box,
  Button,
  Switch,
  Tooltip,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import { useTheme } from '../context/ThemeContext';
import serverLogger from '../utils/ClientLogger';
import { useUserData } from '../auth/UserData';
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import resolveUrlOrigin from '../utils/resolveUrlOrigin';

const Header = () => {
  const { t } = useTranslation();
  const { user, refreshUserData, setUser } = useUserData();
  const { darkMode, toggleDarkMode, tenantConfig } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const backendUrl = window.__BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
  const backendOrigin = useMemo(() => resolveUrlOrigin(backendUrl), [backendUrl]);

  const isWhitelabel = tenantConfig && tenantConfig.slug && tenantConfig.slug !== 'flocktrade';
  const defaultAvatar = isWhitelabel ? null : '/FlockTradeAvatarGB.jpg';

  const getAvatarUrl = (currentUser) => {
    if (!currentUser) return defaultAvatar;
    if (currentUser.avatar && currentUser.avatar.startsWith('http')) return currentUser.avatar;
    if (currentUser.userId && currentUser.avatar) {
      return `https://cdn.discordapp.com/avatars/${currentUser.userId}/${currentUser.avatar}.png`;
    }
    return defaultAvatar;
  };

  const isAuthenticated = Boolean(user);
  const userAvatar = getAvatarUrl(user);

  const pages = [
    { name: t("header.home"), path: '/' },
    { name: t("header.pricing", "Pricing"), path: '/pricing' },
    { name: t("header.sentiment"), path: '/sentiment' },
    { name: t("header.calendar"), path: '/calendar' },
    { name: t("header.signals"), path: '/signals' },
  ];

  useEffect(() => {
    const handleMessage = (event) => {
      // Valider origin hvis vi klarer å resolve en gyldig origin
      if (backendOrigin && event.origin !== backendOrigin) {
        return;
      }

      const eventType = typeof event.data === 'object' ? event.data?.type : event.data;

      if (eventType === 'loggedIn' || eventType === 'discordLinked') {
        serverLogger.log('info', `Authentication event received: ${eventType}`);
        if (eventType === 'loggedIn' && event.data?.user) {
          setUser(event.data.user);
        } else {
          refreshUserData();
        }

        if (eventType === 'loggedIn' && window.location.pathname !== '/app') {
          navigate('/app', { replace: true });
        }
      } else if (eventType === 'loggedOut') {
        serverLogger.log('info', 'Logout message received.');
        refreshUserData();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [backendOrigin, navigate, refreshUserData]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, { withCredentials: true });
      await refreshUserData();
      navigate('/');
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const toggleDrawer = (open) => () => setDrawerOpen(open);
  const handleAvatarClick = () => navigate('/app');

  const MaterialUISwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
      margin: 1,
      padding: 0,
      transform: 'translateX(6px)',
      '&.Mui-checked': {
        color: '#fff',
        transform: 'translateX(22px)',
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            '#fff',
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
      width: 32,
      height: 32,
      '&::before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      borderRadius: 20 / 2,
    },
  }));

  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {pages.map((page) => (
          <ListItem key={page.name} disablePadding>
            <ListItemButton component={Link} to={page.path}>
              <ListItemText primary={page.name} />
            </ListItemButton>
          </ListItem>
        ))}
        {isAuthenticated ? (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText primary={t("header.logout")} />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogin}>
              <ListItemText primary={t("header.login")} />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      color="primary"
      component="nav"
      aria-label="Hovednavigasjon"
      sx={{
        bgcolor: 'rgba(10, 25, 41, 0.8)',
        backdropFilter: 'blur(10px)',
        boxShadow: 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
            <IconButton size="large" color="inherit" onClick={toggleDrawer(true)} aria-label="Åpne navigasjonsmeny">
              <MenuIcon />
            </IconButton>
            <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
              {drawerContent}
            </Drawer>
          </Box>

          {/* Logo center (mobile) / left (desktop) */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Avatar
                alt={tenantConfig?.name || 'FlockTrade'}
                src={tenantConfig?.logo || (isWhitelabel ? undefined : '/FlockTradeAvatar.png')}
                sx={{ width: 50, height: 50, transform: 'scale(2)', bgcolor: tenantConfig?.logo ? 'transparent' : '#2563eb', fontWeight: 700, fontSize: 20 }}
              >
                {!tenantConfig?.logo && (tenantConfig?.name || 'F')[0]}
              </Avatar>
            </Link>
          </Box>

          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              marginLeft: -10,
              color: 'inherit',
              display: { xs: 'none', md: 'flex' },
            }}
          >
            {tenantConfig?.name || 'FlockTrade'}
          </Typography>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button key={page.name} component={Link} to={page.path} sx={{ my: 2, color: 'white' }}>
                {page.name}
              </Button>
            ))}
          </Box>

          {/* User Menu and Avatar */}
          <Box sx={{ flexGrow: 0 }}>
            {isAuthenticated ? (
              <Tooltip title={t('header.dashboard')}>
                <IconButton onClick={handleAvatarClick} sx={{ p: 0 }}>
                  <Avatar alt="User Avatar" src={userAvatar} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button color="inherit" onClick={handleLogin}>
                {t("header.login")}
              </Button>
            )}
          </Box>

          {/* Dark/Light Mode */}
          <MaterialUISwitch sx={{ m: 1 }} checked={darkMode} onChange={toggleDarkMode} inputProps={{ 'aria-label': 'Bytt mellom mørkt og lyst tema' }} />
          <LanguageSwitcher />
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
